/** @jsx hh */
/** @jsxFrag Fragment */

import { isArray } from "@utils/types";

declare global {
  type OptionalKeys<T> = { [P in keyof T]-?: undefined extends T[P] ? P : never }[keyof T];
  type ReqKeys<T> = { [P in keyof T]-?: undefined extends T[P] ? never : P }[keyof T];

  type Merge<A, B> =
    & A
    & {
      [K in Exclude<ReqKeys<B>, keyof A>]: B[K];
    }
    & {
      [K in Exclude<OptionalKeys<B>, keyof A>]?: B[K];
    };

  type Dispatch_msg_map<M extends { [index: string]: any }> = {
    [Key in keyof M]: Merge<{ type: Key; κ?: boolean }, Parameters<M[Key]>[0]>;
  };

  type Dispatch_msg<T extends { [key: string]: any }> = Dispatch_msg_map<
    T
  >[keyof Dispatch_msg_map<T>];

  type Dispatch_map<M extends { [index: string]: any }> = {
    [Key in keyof M]: {
      (props: Dispatch_msg<M>, ε?: any | any[]): ReturnType<M[Key]>;
    };
  };
  // #endregion

  /** δ:delta:dispatch */
  type Dispatch<T extends { [key: string]: any }> = Dispatch_map<T>[keyof Dispatch_map<T>];

  /** ζ:zeta:state */
  type State = any;

  /** ξ:xi:ctx:state, dispatch, render */
  interface Xi<T extends { [key: string]: any } = any, S = any> {
    state: () => S;
    dispatch: Dispatch<T>;
    render: () => any;
  }

  /** ψ:psi:props */
  interface ChiProps {
    run?: boolean;
    [key: string]: any;
  }

  /** ε:eps:elements */
  type ChiElem = any[];

  /** χ:chi:fn/expre */
  interface Chi<T extends ChiProps, R = any> {
    (props: T, children: ChiElem): R;
  }

  /** λ:lambda:composed-fn */
  interface ChiFn<T extends ChiProps = any, R = any> {
    chi: Chi<T, R> | string;
    props: T;
    children: ChiElem;
  }

  /** base/engine handlers */
  interface Ξhandlers {
    run: (ψ: any, ε: any[]) => (λ: ChiFn) => Xi;
    idk: (ψ?: any, ε?: any[]) => (λ: ChiFn) => Xi;
    render: (ψ?: any, ε?: any[]) => any;
  }

  /** Ξ:xi:root context */
  export type ChiRoot = Xi<Ξhandlers, any>;

  export namespace JSX {
    interface IntrinsicElements {
      [elemName: string]: any;
    }
    interface IntrinsicAttributes {
      key?: string | number;
    }
  }
}

/** eval λ expression */
export const ChiEval = (chi: ChiFn) => (chi.chi as CallableFunction)(chi.props, chi.children);

/** fragment */
export const ChiFragment = (props: any, ...children: any[]) => children;

/** transform tsx to λ=χ.ψ.ε; if ψ has a truthy κ, it gets immediatly evaled   */
export const hh = (chi: any, props: any, ...children: any[]) => {
  if (props && props.children) {
    if (isArray(children)) {
      children = isArray(props.children)
        ? [...props.children, ...children]
        : [props.children, ...children];
    } else {
      children = isArray(props.children) ? props.children : [props.children];
    }
  }
  if (children.length === 1 && isArray(children[0])) children = children.flat(1);

  const res = { chi, props: props || {}, children };
  return res.props.run ? res.chi(res.props, res.children) : res;
};

export const χκ = (ψ?: any, ε?: any[]) => {
  const dispatch: Dispatch<Ξhandlers> = (ψ, ε = []) => {
    switch (ψ.type) {
      case "run":
        return ChiEval;
      default:
        break;
    }
  };

  const Ξ = { dispatch, state: () => {}, render: () => {} } as ChiRoot;
  return Ξ;
};

χκ();
